import { Property } from "../types/search";

/**
 * Parse server-fetched properties from the HTML
 * This function extracts property data from the server-rendered HTML
 * that was added by serverApp.ts for SEO purposes
 */
export const parseServerFetchedProperties = (): {
  properties: Property[];
  totalCount: number;
} => {
  // Check if we're in a browser environment
  if (typeof document === "undefined") {
    return { properties: [], totalCount: 0 };
  }

  // Check if the initial-properties div exists
  const initialPropertiesDiv = document.getElementById("initial-properties");
  if (!initialPropertiesDiv) {
    return { properties: [], totalCount: 0 };
  }

  // Get total count from data attribute
  const totalCount = parseInt(
    initialPropertiesDiv.getAttribute("data-total-count") || "0",
    10
  );

  // Get all property items
  const propertyItems = initialPropertiesDiv.querySelectorAll(".property-item");
  const properties: Property[] = [];

  propertyItems.forEach((item) => {
    // Parse data attributes
    const id = item.getAttribute("data-id") || "";
    const price = parseInt(item.getAttribute("data-price") || "0", 10);
    const bedrooms = parseInt(item.getAttribute("data-bedrooms") || "0", 10);
    const bathrooms = parseInt(item.getAttribute("data-bathrooms") || "0", 10);
    const sqft = parseInt(item.getAttribute("data-sqft") || "0", 10);
    const status = item.getAttribute("data-status") || "Active";
    const subdivision = item.getAttribute("data-subdivision") || "";
    const yearBuilt = item.getAttribute("data-year-built") || "";
    const postalCode = item.getAttribute("data-postal-code") || "";
    const garageSpaces = parseInt(
      item.getAttribute("data-garage-spaces") || "0",
      10
    );
    const propertyType = item.getAttribute("data-property-type") || "";
    const onMarketDate = item.getAttribute("data-on-market-date") || "";
    const agent = item.getAttribute("data-agent") || "";
    const office = item.getAttribute("data-office") || "";
    const latitude = item.getAttribute("data-latitude") || "";
    const longitude = item.getAttribute("data-longitude") || "";
    const basement = item.getAttribute("data-basement") === "true";
    const pool = item.getAttribute("data-pool") === "true";
    const waterfront = item.getAttribute("data-waterfront") === "true";
    const garage = item.getAttribute("data-garage") === "true";
    const cityView = item.getAttribute("data-city-view") === "true";
    const mountainView = item.getAttribute("data-mountain-view") === "true";
    const parkView = item.getAttribute("data-park-view") === "true";
    const waterView = item.getAttribute("data-water-view") === "true";
    const newConstruction =
      item.getAttribute("data-new-construction") === "true";
    const rental = item.getAttribute("data-rental") === "true";
    const mlsBoardCode = item.getAttribute("data-mls-board-code") || "";
    const listingKey = item.getAttribute("data-listing-key") || "";

    // Compliance fields
    const listprice = item.getAttribute("data-listprice") || "";
    const listpricelow = item.getAttribute("data-listpricelow") || "";
    const mlsstatus = item.getAttribute("data-mlsstatus") || "";
    const internetAddressDisplayYN =
      item.getAttribute("data-internet-address-display") === "true";
    const mustRemovePrimaryPhoto =
      item.getAttribute("data-must-remove-primary-photo") === "true";
    const mustRemovePhotos =
      item.getAttribute("data-must-remove-photos") === "true";
    const showMapLink = item.getAttribute("data-show-map-link") !== "false"; // Default to true

    // Get address elements
    const addressElement = item.querySelector(".property-address");
    const address = addressElement ? addressElement.textContent || "" : "";
    const addressParts = address.split(", ");
    const street = addressParts[0] || "";
    const city = addressParts[1] || "";
    const stateZip = addressParts[2] || "";
    const stateZipParts = stateZip.split(" ");
    const state = stateZipParts[0] || "";
    const zipcode = stateZipParts[1] || "";

    // Get image URL if available
    const imageElement = item.querySelector("img");
    const imageUrl = imageElement ? imageElement.getAttribute("src") || "" : "";

    // Create property object
    const property: Property = {
      id: listingKey,
      mlsNumber: id,
      mlsBoardCode,
      title: `${propertyType} in ${city}, ${state}`,
      price,
      location: `${city}, ${state}`,
      propertyType,
      address: {
        full: address,
        city,
        state,
        zipcode: postalCode,
        coordinates:
          latitude && longitude
            ? {
                lat: parseFloat(latitude),
                lng: parseFloat(longitude),
              }
            : undefined,
      },
      property: {
        bathroomsTotal: bathrooms,
        bedroomsTotal: bedrooms,
        garageSpaces,
        hasBasement: basement,
        hasPool: pool,
        isCityView: cityView,
        isMountainView: mountainView,
        isParkView: parkView,
        isWaterFront: waterfront,
        isWaterView: waterView,
        isNewConstruction: newConstruction,
        isRental: rental,
        latitude,
        longitude,
        livingArea: sqft,
        listprice,
        listpricelow,
        propertySubType: [propertyType],
        propertyType,
        subdivisionName: subdivision,
        yearBuilt: yearBuilt ? parseInt(yearBuilt, 10) : undefined,
      },
      internetAddressDisplayYN,
      displayRules: {
        mustRemovePrimaryPhoto,
        mustRemovePhotos,
        showMapLink,
      },
      listprice,
      listpricelow,
      mlsstatus,
      details: {
        bedrooms,
        bathrooms,
        squareFeet: sqft,
      },
      bedrooms,
      bathrooms,
      squareFeet: sqft,
      images: imageUrl ? [imageUrl] : [],
      photos: imageUrl ? [imageUrl] : [],
      features: [],
      listingStatus: status,
      listingOffice: {
        name: office,
        email: "",
        phone: "",
        id: "",
        key: "",
      },
    };

    properties.push(property);
  });

  return { properties, totalCount };
};

/**
 * Custom hook to use server-fetched properties
 * This function checks if server-fetched properties are available and returns them
 * It's designed to be used with direct search (no query) on initial page load
 */
export const useServerFetchedProperties = (
  isDirectSearch: boolean
): {
  serverProperties: Property[];
  serverTotalCount: number;
  hasServerProperties: boolean;
} => {
  // Only parse server properties if this is a direct search
  if (isDirectSearch) {
    const { properties, totalCount } = parseServerFetchedProperties();
    return {
      serverProperties: properties,
      serverTotalCount: totalCount,
      hasServerProperties: properties.length > 0,
    };
  }

  // Return empty values if not a direct search
  return {
    serverProperties: [],
    serverTotalCount: 0,
    hasServerProperties: false,
  };
};
